import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import paymentsService from '@/services/payments/paymentsService';

// Function to dynamically load the Razorpay checkout script
const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

const PaymentPage = () => {
    const { enrollmentId } = useParams<{ enrollmentId: string }>();
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [paymentResult, setPaymentResult] = useState<{
        success: boolean;
        paymentId?: string;
        orderId?: string;
    } | null>(null);

    // 1. Fetch details
    const { data: detailsResponse, isLoading: isLoadingDetails, error: detailsError, refetch } = useQuery({
        queryKey: ['payment-details', enrollmentId],
        queryFn: () => paymentsService.getPaymentDetails(enrollmentId!),
        enabled: !!enrollmentId,
    });

    const details = detailsResponse?.data;

    // 2. Load script on mount
    useEffect(() => {
        loadRazorpayScript().then((res) => {
            setScriptLoaded(res);
        });
    }, []);

    // 3. Verify Payment Mutation
    const verifyMutation = useMutation({
        mutationFn: (payload: {
            enrollmentId: string;
            orderId: string;
            paymentId: string;
            signature: string;
        }) => paymentsService.verifyPayment(payload),
        onSuccess: (res) => {
            setIsVerifying(false);
            setPaymentResult({
                success: true,
                paymentId: verifyMutation.variables?.paymentId,
                orderId: verifyMutation.variables?.orderId,
            });
            toast.success(res.message || 'Payment successfully processed!');
            void refetch(); // Refresh to get the generated LOA path
        },
        onError: (err: any) => {
            setIsVerifying(false);
            toast.error(err?.response?.data?.message || 'Payment verification failed');
            setPaymentResult({
                success: false,
            });
        },
    });

    // 4. Create Order Mutation
    const orderMutation = useMutation({
        mutationFn: () => paymentsService.createOrder(enrollmentId!),
        onSuccess: (res) => {
            const orderData = res.data;
            
            if (orderData.is_mock) {
                // In mock mode, bypass Razorpay Checkout and go straight to verification with dummy details
                toast.success('Simulating Mock Payment Flow...');
                setIsVerifying(true);
                verifyMutation.mutate({
                    enrollmentId: enrollmentId!,
                    orderId: orderData.order_id,
                    paymentId: `mock_pay_${Date.now()}`,
                    signature: 'mock_signature_bypass_for_dev',
                });
                return;
            }

            if (!scriptLoaded || !(window as any).Razorpay) {
                toast.error('Razorpay SDK failed to load. Please check your internet connection.');
                return;
            }

            // Open Razorpay Standard Checkout
            const options = {
                key: orderData.key_id,
                amount: orderData.amount * 100, // paise
                currency: orderData.currency,
                name: 'AI Fellowship Program',
                description: `Enrollment Fee - ${orderData.cohort_name}`,
                order_id: orderData.order_id,
                prefill: {
                    name: orderData.student.name,
                    email: orderData.student.email,
                    contact: orderData.student.contact,
                },
                theme: {
                    color: '#017DF2', // Match application primary color
                },
                handler: (response: any) => {
                    setIsVerifying(true);
                    verifyMutation.mutate({
                        enrollmentId: enrollmentId!,
                        orderId: response.razorpay_order_id,
                        paymentId: response.razorpay_payment_id,
                        signature: response.razorpay_signature,
                    });
                },
                modal: {
                    ondismiss: () => {
                        toast.error('Payment cancelled by student');
                    },
                },
            };

            const rzp = new (window as any).Razorpay(options);
            rzp.open();
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message || 'Failed to create payment order');
        },
    });

    if (!enrollmentId) {
        return (
            <div className="min-h-screen bg-[var(--body-bg-color)] flex items-center justify-center p-4">
                <div className="bg-white p-6 rounded-2xl border text-center shadow-sm">
                    <span className="text-[var(--semantic-error-color)] font-semibold">Invalid Payment Link</span>
                </div>
            </div>
        );
    }

    if (isLoadingDetails) {
        return (
            <div className="min-h-screen bg-[var(--body-bg-color)] flex items-center justify-center p-4">
                <div className="text-sm text-[var(--disabled-text-color)] font-medium">Retrieving payment invoice...</div>
            </div>
        );
    }

    if (detailsError || !details) {
        return (
            <div className="min-h-screen bg-[var(--body-bg-color)] flex items-center justify-center p-4">
                <div className="bg-white p-6 rounded-2xl border text-center shadow-sm max-w-sm">
                    <span className="text-[var(--semantic-error-color)] font-semibold block mb-2">Invoice Not Found</span>
                    <p className="text-xs text-[var(--disabled-text-color)]">This payment link may have expired or the enrollment record is invalid.</p>
                </div>
            </div>
        );
    }

    const isPaid = details.status === 'PAYMENT_COMPLETED' || details.status === 'ENROLLED';

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-3xl border border-slate-100 shadow-xl relative overflow-hidden">
                {/* Decorative background gradients */}
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500" />
                
                {/* Header */}
                <div className="text-center">
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">
                        AI Fellowship Program
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 font-medium">
                        Enrollment Fee Payment Gateway
                    </p>
                </div>

                {/* Success State */}
                {(paymentResult?.success || isPaid) ? (
                    <div className="mt-6 text-center space-y-4">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 border border-emerald-100">
                            <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800">Fee Payment Completed!</h3>
                        <p className="text-xs text-slate-500 leading-relaxed px-4">
                            We have successfully registered your fee payment of <strong>₹{Number(details.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>. Your admission to the fellowship is now locked.
                        </p>

                        <div className="bg-slate-50 p-4 rounded-2xl border text-left space-y-2 text-xs font-medium text-slate-600">
                            <div className="flex justify-between">
                                <span>Student Name:</span>
                                <span className="font-semibold text-slate-800">{details.student_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Enrollment No:</span>
                                <span className="font-semibold font-mono text-slate-800">{details.enrollment_number}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Cohort Name:</span>
                                <span className="font-semibold text-slate-800">{details.cohort_name}</span>
                            </div>
                            {paymentResult?.paymentId && (
                                <div className="flex justify-between">
                                    <span>Transaction ID:</span>
                                    <span className="font-semibold font-mono text-slate-800">{paymentResult.paymentId}</span>
                                </div>
                            )}
                        </div>

                        {/* Letter of Registration download */}
                        <div className="pt-2">
                            <button
                                onClick={() => {
                                    const path = details.lor_path;
                                    if (path) {
                                        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1';
                                        const backendBaseUrl = apiBaseUrl.replace('/api/v1', '');
                                        const fullUrl = `${backendBaseUrl}/api/${path}`;
                                        window.open(fullUrl, '_blank');
                                    } else {
                                        toast.error('LOA is compiling. Please check your email inbox.');
                                    }
                                }}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 cursor-pointer"
                            >
                                Download Letter of Registration (LOA)
                            </button>
                        </div>
                    </div>
                ) : isVerifying ? (
                    <div className="mt-6 text-center py-6 space-y-3">
                        <div className="mx-auto animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
                        <h3 className="text-sm font-bold text-slate-700">Verifying Transaction Signature...</h3>
                        <p className="text-xs text-slate-400">Please do not refresh or close this browser window.</p>
                    </div>
                ) : (
                    /* Invoice Checkout state */
                    <div className="mt-6 space-y-6">
                        <div className="bg-slate-50 p-5 rounded-2xl border space-y-3 text-xs md:text-sm font-medium text-slate-600">
                            <div className="flex justify-between border-b pb-2">
                                <span>Student Name:</span>
                                <span className="font-semibold text-slate-800">{details.student_name}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span>Enrollment No:</span>
                                <span className="font-semibold font-mono text-slate-800">{details.enrollment_number}</span>
                            </div>
                            <div className="flex justify-between border-b pb-2">
                                <span>Cohort:</span>
                                <span className="font-semibold text-slate-800 text-right max-w-[200px]">{details.cohort_name}</span>
                            </div>
                            <div className="flex justify-between text-base pt-1 text-slate-800 font-bold">
                                <span>Amount Due:</span>
                                <span className="text-blue-600">₹{Number(details.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                        </div>

                        <div>
                            <button
                                onClick={() => orderMutation.mutate()}
                                disabled={orderMutation.isPending}
                                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer transition-colors"
                            >
                                {orderMutation.isPending ? 'Connecting Gateway...' : `Pay ₹${Number(details.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })} via Razorpay`}
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer brand */}
                <div className="text-center pt-2">
                    <span className="text-[10px] text-slate-400 font-medium">Secure Payment Processing powered by Razorpay</span>
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;
